import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addMonths, subMonths } from "date-fns";
import esES from "date-fns/locale/es";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import "react-big-calendar/lib/css/react-big-calendar.css";

const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) => format(date, formatStr, { ...options, locale: esES }),
  parse: (value, formatStr, options) => parse(value, formatStr, new Date(), { ...options, locale: esES }),
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1, locale: esES }),
  getDay,
  locales: { es: esES },
});

const CustomEvent = ({ event }) => (
  <div>
    <div style={{ fontWeight: 600 }}>{event.title}</div>
    {event.descripcion && (
      <div style={{ fontSize: "0.97em", color: "#333" }}>{event.descripcion}</div>
    )}
  </div>
);

export const MiCalendario = () => {
  const { auth } = useAuth();
  const calendarRef = useRef();
  const [todosLosTurnos, setTodosLosTurnos] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [plantillas, setPlantillas] = useState([]);
  const [vistaActual, setVistaActual] = useState("week");

  const ahora = new Date();
  const [rangoCargado, setRangoCargado] = useState(() => {
    const desde = subMonths(new Date(ahora.getFullYear(), ahora.getMonth(), 1), 3);
    const hasta = addMonths(new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0), 4);
    return { desde, hasta };
  });

  const [rangoVisible, setRangoVisible] = useState(null);

  useEffect(() => {
    const cargarPlantillas = async () => {
      try {
        const res = await fetch(Global.url + "turnos/plantillas", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        setPlantillas(data);
      } catch {}
    };
    cargarPlantillas();
  }, []);

  const fetchTurnos = (desde, hasta) => {
    if (!auth || !auth.id) return;
    setLoading(true);
    fetch(
      Global.url +
        `turnos?usuario_id=${auth.id}&desde=${desde.toISOString().slice(0, 10)}&hasta=${hasta.toISOString().slice(0, 10)}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        const turnos = data.map((turno) => ({
          id: turno.id,
          title: turno.titulo ||
            (turno.tipo === "ausencia"
              ? turno.descripcion || "Ausencia"
              : "Turno laboral"),
          start: new Date(turno.inicio.replace(" ", "T")),
          end: new Date(turno.fin.replace(" ", "T")),
          allDay: false,
          activo: turno.activo,
          tipo: turno.tipo,
          descripcion: turno.descripcion,
          plantilla_id: turno.plantilla_id,
        }));
        setTodosLosTurnos(turnos);
        setRangoCargado({ desde, hasta });
      })
      .catch(() => setMensaje("Error de conexión"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!auth || !auth.id) return;
    const desde = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
    const hasta = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0);
    setRangoVisible({ desde, hasta });
    fetchTurnos(rangoCargado.desde, rangoCargado.hasta);
    // eslint-disable-next-line
  }, [auth]);

  useEffect(() => {
    if (vistaActual === "day") {
      const visibleDate = calendarRef.current?.state?.currentDate || new Date();
      const desde = new Date(visibleDate);
      const hasta = new Date(visibleDate);
      hasta.setHours(23, 59, 59, 999);
      setRangoVisible({ desde, hasta });
    }
  }, [vistaActual]);

  useLayoutEffect(() => {
    if (!rangoVisible) return;
    setEventos(
      todosLosTurnos.filter(
        evt => evt.start <= rangoVisible.hasta && evt.end >= rangoVisible.desde
      )
    );
  }, [rangoVisible, todosLosTurnos]);

  const handleRangeChange = (range) => {
    let desde, hasta;
    if (Array.isArray(range)) {
      desde = range[0];
      hasta = range[range.length - 1];
    } else if (range.start && range.end) {
      desde = range.start;
      hasta = range.end;
    } else if (range instanceof Date) {
      desde = new Date(range);
      hasta = new Date(range);
      hasta.setHours(23, 59, 59, 999);
    } else {
      const hoy = new Date();
      desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    }

    setRangoVisible({ desde, hasta });

    if (desde < rangoCargado.desde || hasta > rangoCargado.hasta) {
      const nuevoCentro = new Date(desde.getFullYear(), desde.getMonth(), 1);
      const nuevoDesde = subMonths(nuevoCentro, 3);
      const nuevoHasta = addMonths(new Date(nuevoCentro.getFullYear(), nuevoCentro.getMonth() + 1, 0), 4);
      fetchTurnos(nuevoDesde, nuevoHasta);
    }
  };

  const eventPropGetter = (event) => {
    let estilo = {};
    if (event.plantilla_id) {
      const plantilla = plantillas.find(p => String(p.id) === String(event.plantilla_id));
      if (plantilla && plantilla.color) {
        estilo.backgroundColor = plantilla.color;
        estilo.color = "#222";
      }
    }
    if (event.tipo === "ausencia") {
      estilo.backgroundColor = "#ffbaba";
      estilo.color = "#fff";
    }
    if (!event.activo) {
      estilo.backgroundColor = "#ddd";
      estilo.color = "#888";
      estilo.textDecoration = "line-through";
    }
    if (!estilo.backgroundColor) {
      estilo.backgroundColor = "#85C1E9";
      estilo.color = "#222";
    }
    return { style: estilo };
  };

  return (
    <div className="consulta-turnos" style={{ height: 600 }}>
      <h2 className="centrar-titulo">Mis Turnos Laborales</h2>
      {mensaje && <p className="rojo">{mensaje}</p>}
      <Calendar
        ref={calendarRef}
        localizer={localizer}
        events={eventos}
        startAccessor="start"
        endAccessor="end"
        titleAccessor="title"
        defaultView="week"
        views={['day', 'week', 'month', 'agenda']}
        onView={setVistaActual}
        onRangeChange={handleRangeChange}
        scrollToTime={new Date(1970, 1, 1, 7, 30)}
        min={new Date(1970, 1, 1, 6, 0)}
        max={new Date(1970, 1, 1, 22, 0)}
        step={15}
        timeslots={4}
        style={{ height: "100%" }}
        eventPropGetter={eventPropGetter}
        components={{ event: CustomEvent }}
        messages={{
          date: "Fecha",
          time: "Hora",
          event: "Evento",
          allDay: "Todo el día",
          week: "Semana",
          work_week: "Semana laboral",
          day: "Día",
          month: "Mes",
          previous: "Ant",
          next: "Sig",
          yesterday: "Ayer",
          tomorrow: "Mañana",
          today: "Hoy",
          agenda: "Agenda",
          showMore: total => `+ Ver ${total} más`
        }}
      />
      {loading && <p>Cargando turnos...</p>}
    </div>
  );
};
